import React, { useEffect, useState } from "react";
import {
  Button,
  Fade,
  TextField,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  FormLabel,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Autocomplete from "@material-ui/lab/Autocomplete";
import axios from "axios";
import { toast } from "react-toastify";
import { decryptData } from "../../../../crypto";
import useStyles from "../../../../styles";
import { getCourseData } from "../../../../service/ExamServices";
import Loader from "./Loader";

const DuplicateExamPopup = ({ open, handleClose, selectedRowData, fetchData }) => {
  const classes = useStyles();
  const decryptedToken = decryptData(sessionStorage.getItem("token"));

  const [examName, setExamName] = useState("");
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [center, setCenter] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState([]);
  const [duration, setDuration] = useState("");
  const [enableSubmit, setEnableSubmit] = useState("0");
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [loading, setLoading] = useState(false);

  // Load courses on mount
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await getCourseData();
        if (res?.courses) {
          setCourses(res.courses);
        }
      } catch (err) {
        console.error("Failed to load courses:", err);
      }
    };
    fetchCourses();
  }, []);

  // Reset all fields to empty when dialog opens
  useEffect(() => {
    if (open) {
      setExamName("");
      setDuration("");
      setEnableSubmit("0");
      setStartDate("");
      setStartTime("");
      setEndTime("");
      setSelectedCourse([]);
      setSelectedBatch([]);
      setSelectedCenter([]);
    }
  }, [open]);

  // Fetch batches when selectedCourse changes
  useEffect(() => {
    if (selectedCourse.length > 0) {
      fetchBatches(selectedCourse);
    } else {
      setBatches([]);
      setSelectedBatch([]);
    }
  }, [selectedCourse]);

  // Fetch centers when selectedCourse and selectedBatch change
  useEffect(() => {
    if (selectedCourse.length > 0 && selectedBatch.length > 0) {
      fetchCenter();
    } else {
      setCenter([]);
      setSelectedCenter([]);
    }
  }, [selectedCourse, selectedBatch]);

  const fetchBatches = async (courseIds) => {
    const data = {
      batch: {
        course_ids: courseIds,
      },
    };
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/batch/by_course_ids/list`,
        data,
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        }
      );
      if (response && response.data.batches) {
        setBatches(response.data.batches);
      }
    } catch (error) {
      console.error("Error fetching batches:", error);
    }
  };

  const fetchCenter = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/client/center/centers/course/batch/new`,
        {
          center: {
            courseId: selectedCourse,
            batchId: selectedBatch,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (response && response.data.response) {
        setCenter(response.data.response);
      }
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  };

  const handleSubmit = async () => {
    if (!examName.trim()) {
      toast.warn("Please enter an exam name.");
      return;
    }
    if (selectedCourse.length === 0) {
      toast.warn("Please select at least one course.");
      return;
    }
    if (selectedBatch.length === 0) {
      toast.warn("Please select at least one batch.");
      return;
    }
    if (selectedCenter.length === 0) {
      toast.warn("Please select at least one branch.");
      return;
    }
    if (!duration) {
      toast.warn("Please enter a duration.");
      return;
    }
    if (!startDate) {
      toast.warn("Please select a start date.");
      return;
    }
    if (!startTime) {
      toast.warn("Please select a start time.");
      return;
    }
    if (!endTime) {
      toast.warn("Please select an end time.");
      return;
    }

    setLoading(true);
    try {
      // Body payload for duplicating an exam
      const payload = {
        id: selectedRowData.id,
        exam_name: examName,
        course_ids: selectedCourse,
        batch_ids: selectedBatch,
        center_ids: selectedCenter,
        duration: Number(duration),
        enable_submit: enableSubmit,
        start_date: startDate,
        start_time: startTime,
        end_time: endTime,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/duplicate`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200 || response.data.success) {
        toast.success("Exam duplicated successfully!");
        fetchData();
        handleClose();
      } else {
        toast.error(response.data.message || "Failed to duplicate exam.");
      }
    } catch (error) {
      const errMsg = error.response?.data?.message || "Error duplicating exam.";
      toast.error(errMsg);
      console.error("Duplicate error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Loader message="Duplicating exam, please wait..." />}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        style: { borderRadius: "12px", padding: "16px" },
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DialogTitle style={{ padding: "8px 24px 8px 8px" }}>
          <Typography variant="h6" className={classes.textcolorformhead} style={{ fontWeight: 600 }}>
            Duplicate Exam
          </Typography>
        </DialogTitle>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <DialogContent dividers>
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {/* Row 1 */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Exam Name <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                value={examName}
                onChange={(e) => setExamName(e.target.value)}
                placeholder="Enter Exam Name"
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Course <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <Autocomplete
                multiple
                options={courses}
                getOptionLabel={(option) => option.course_name}
                value={courses.filter((c) => selectedCourse.includes(c.id))}
                onChange={(event, newValue) =>
                  setSelectedCourse(newValue.map((c) => c.id))
                }
                disableClearable
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select Course"
                  />
                )}
              />
            </div>
          </div>

          {/* Row 2 */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Batch <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <Autocomplete
                multiple
                options={batches}
                getOptionLabel={(option) => option.batch_name}
                value={batches.filter((b) => selectedBatch.includes(b.id))}
                onChange={(event, newValue) =>
                  setSelectedBatch(newValue.map((b) => b.id))
                }
                disableClearable
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select Batch"
                  />
                )}
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Branch <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <Autocomplete
                multiple
                options={center}
                getOptionLabel={(option) => `${option.center_code} - ${option.center_name}`}
                value={center.filter((c) => selectedCenter.includes(c.id))}
                onChange={(event, newValue) =>
                  setSelectedCenter(newValue.map((c) => c.id))
                }
                disableClearable
                forcePopupIcon={false}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Select Branch"
                  />
                )}
              />
            </div>
          </div>

          {/* Row 3 */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Duration (minutes) <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <TextField
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                placeholder="Enter Duration"
                inputProps={{ min: 1 }}
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Submit Enable (minutes)
              </FormLabel>
              <TextField
                type="number"
                variant="outlined"
                fullWidth
                size="small"
                value={enableSubmit}
                onChange={(e) => setEnableSubmit(e.target.value)}
                placeholder="Enter minutes"
                inputProps={{ min: 0 }}
              />
            </div>
          </div>

          {/* Row 4 */}
          <div style={{ display: "flex", gap: "16px" }}>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Start Date <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <TextField
                type="date"
                variant="outlined"
                fullWidth
                size="small"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                Start Time <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <TextField
                type="time"
                variant="outlined"
                fullWidth
                size="small"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
              <FormLabel className={classes.textcolorformlabel} style={{ marginBottom: "8px" }}>
                End Time <span className={classes.textcolorred}>*</span>
              </FormLabel>
              <TextField
                type="time"
                variant="outlined"
                fullWidth
                size="small"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        </div>

        <div
          className={`${classes.dflex} ${classes.justifyflexend} ${classes.mt1}`}
          style={{ marginTop: "24px" }}
        >
          <Button
            className={classes.custombtnoutline}
            onClick={handleClose}
            style={{ marginRight: "8px" }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            className={classes.custombtnblue}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Duplicating..." : "Submit"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default DuplicateExamPopup;
